import {ParamMap, Params} from '@angular/router';
import {
  radioBoxTriState,
  radioBoxTriStateToQuery,
  RadioBoxValue,
} from '@components/radio-box/radio-box-filter.util';

export const FARMER_LIST_FILTER_QUERY_KEYS = {
  regionId: 'regionId',
  fieldId: 'fieldId',
  cropId: 'cropId',
  createAtFrom: 'createAtFrom',
  createAtTo: 'createAtTo',
  sourceKind: 'sourceKind',
  anomalyType: 'anomalyType',
  satelliteAnomalyType: 'satelliteAnomalyType',
  reportState: 'reportState',
  rating: 'rating',
  takenIntoAccount: 'takenIntoAccount',
} as const;

export type FarmerListFilterModel = {
  regionId?: string | null;
  fieldId?: string | null;
  cropId?: string | null;
  createAt?: Date[] | null;
  sourceKind?: string | null;
  anomalyType?: string | null;
  satelliteAnomalyType?: number | null;
  reportState?: number[] | null;
  rating?: number[] | null;
  isTakenIntoAccount?: RadioBoxValue;
};

export function createEmptyFarmerListFilterModel(): FarmerListFilterModel {
  return {
    isTakenIntoAccount: null,
  };
}

export function parseFarmerListFilterQuery(params: ParamMap): FarmerListFilterModel {
  const model = createEmptyFarmerListFilterModel();
  const regionId = params.get(FARMER_LIST_FILTER_QUERY_KEYS.regionId);
  const fieldId = params.get(FARMER_LIST_FILTER_QUERY_KEYS.fieldId);
  const cropId = params.get(FARMER_LIST_FILTER_QUERY_KEYS.cropId);
  if (regionId) model.regionId = regionId;
  if (fieldId) model.fieldId = fieldId;
  if (cropId) model.cropId = cropId;

  const from = parseQueryDate(params.get(FARMER_LIST_FILTER_QUERY_KEYS.createAtFrom));
  const to = parseQueryDate(params.get(FARMER_LIST_FILTER_QUERY_KEYS.createAtTo));
  if (from || to) {
    model.createAt = [from as Date, to as Date];
  }

  const sourceKind = params.get(FARMER_LIST_FILTER_QUERY_KEYS.sourceKind);
  if (sourceKind) model.sourceKind = sourceKind;
  const anomalyType = params.get(FARMER_LIST_FILTER_QUERY_KEYS.anomalyType);
  if (anomalyType) model.anomalyType = anomalyType;
  const sat = params.get(FARMER_LIST_FILTER_QUERY_KEYS.satelliteAnomalyType);
  if (sat != null && sat !== '') {
    const n = Number(sat);
    if (Number.isInteger(n)) model.satelliteAnomalyType = n;
  }

  const states = parseCsvInts(params.get(FARMER_LIST_FILTER_QUERY_KEYS.reportState));
  if (states.length) model.reportState = states;
  const ratings = parseCsvInts(params.get(FARMER_LIST_FILTER_QUERY_KEYS.rating));
  if (ratings.length) model.rating = ratings;

  const taken = params.get(FARMER_LIST_FILTER_QUERY_KEYS.takenIntoAccount);
  if (taken === 'yes' || taken === 'no') {
    model.isTakenIntoAccount = radioBoxTriState(taken);
  }

  return model;
}

export function serializeFarmerListFilterQuery(model: FarmerListFilterModel): Params {
  const [from, to] = splitDateRange(model.createAt);
  const taken = radioBoxTriStateToQuery(model.isTakenIntoAccount);

  return {
    [FARMER_LIST_FILTER_QUERY_KEYS.regionId]: model.regionId || null,
    [FARMER_LIST_FILTER_QUERY_KEYS.fieldId]: model.fieldId || null,
    [FARMER_LIST_FILTER_QUERY_KEYS.cropId]: model.cropId || null,
    [FARMER_LIST_FILTER_QUERY_KEYS.createAtFrom]: from,
    [FARMER_LIST_FILTER_QUERY_KEYS.createAtTo]: to,
    [FARMER_LIST_FILTER_QUERY_KEYS.sourceKind]: model.sourceKind || null,
    [FARMER_LIST_FILTER_QUERY_KEYS.anomalyType]: model.anomalyType || null,
    [FARMER_LIST_FILTER_QUERY_KEYS.satelliteAnomalyType]:
      model.satelliteAnomalyType != null ? String(model.satelliteAnomalyType) : null,
    [FARMER_LIST_FILTER_QUERY_KEYS.reportState]: model.reportState?.length
      ? model.reportState.join(',')
      : null,
    [FARMER_LIST_FILTER_QUERY_KEYS.rating]: model.rating?.length ? model.rating.join(',') : null,
    [FARMER_LIST_FILTER_QUERY_KEYS.takenIntoAccount]: taken,
  };
}

function parseCsvInts(raw: string | null): number[] {
  if (!raw) {
    return [];
  }
  return raw
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isInteger(n));
}

function parseQueryDate(raw: string | null): Date | null {
  if (!raw) {
    return null;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!match) {
    return null;
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function splitDateRange(range: Date[] | null | undefined): [string | null, string | null] {
  if (!range?.length) {
    return [null, null];
  }
  const from = range[0] ? formatYmd(range[0]) : null;
  const to = range[1] ? formatYmd(range[1]) : null;
  return [from, to];
}

function formatYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
